import React, { useEffect, useMemo, useState } from 'react';
import RichTextEditor from './RichTextEditor';
import {
  adminCreateBlog,
  adminGetBlog,
  adminUpdateBlog,
  adminUploadBlogImage,
} from '../../services/blogs';

const Ic = ({ d, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable="false">
    {(Array.isArray(d) ? d : [d]).map((p, i) => <path key={i} d={p} />)}
  </svg>
);

const ICONS = {
  back:   'M19 12H5M12 19l-7-7 7-7',
  save:   ['M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z','M17 21v-8H7v8','M7 3v5h8'],
  upload: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4','M17 8l-5-5-5 5','M12 3v12'],
  pub:    ['M3 15a4 4 0 0 0 4 4h9a5 5 0 1 0-.1-9.999 5.002 5.002 0 1 0-9.78 2.096A4.001 4.001 0 0 0 3 15'],
  del:    ['M3 6h18','M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 0-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
};

const ACCESS_LEVELS = [
  { value: 'public',        label: 'Public — anyone can read' },
  { value: 'authenticated', label: 'Logged-in users only' },
  { value: 'premium',       label: 'Premium — course subscribers only' },
];

const STATUSES = [
  { value: 'draft',     label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived',  label: 'Archived' },
];

const KNOWN_COURSES = [
  { id: 'stock-market-foundation',            label: 'Stock Market Foundation' },
  { id: 'investing-wealth-creation',          label: 'Investing & Wealth Creation' },
  { id: 'technical-analysis',                 label: 'Technical Analysis' },
  { id: 'trading-psychology-risk-management', label: 'Trading Psychology & Risk' },
  { id: 'derivatives-options-basics',         label: 'Derivatives & Options Basics' },
  { id: 'invfly-swing-trader',                label: 'InvFly Swing Trader' },
  { id: 'invfly-pro-trader',                  label: 'InvFly Pro Trader' },
  { id: 'college-university-nep',             label: 'College & University (NEP)' },
];

function autoSlug(title) {
  return (title || '').toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').slice(0, 80);
}

const empty = {
  title: '', slug: '', excerpt: '', cover_image: '', cover_image_alt: '',
  content_html: '', content_json: null, category: '', tags: [],
  status: 'draft', access_level: 'public', required_courses: [],
  seo: { meta_title: '', meta_description: '', og_image: '' },
};

const BlogEditor = ({ token, blogId, onClose, onSaved }) => {
  const isEdit = Boolean(blogId);
  const [form,           setForm]           = useState(empty);
  const [loading,        setLoading]        = useState(isEdit);
  const [saving,         setSaving]         = useState(false);
  const [error,          setError]          = useState('');
  const [tagInput,       setTagInput]       = useState('');
  const [coverUploading, setCoverUploading] = useState(false);
  const [slugTouched,    setSlugTouched]    = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await adminGetBlog(token, blogId);
        if (cancelled) return;
        const d = res.data?.data || {};
        setForm({ ...empty, ...d, tags: d.tags || [], required_courses: d.required_courses || [], seo: d.seo || empty.seo });
        setSlugTouched(true);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.message || 'Failed to load blog');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [blogId, isEdit, token]);

  const set = (patch) => setForm(f => ({ ...f, ...patch }));

  const onTitleChange = (e) => {
    const title = e.target.value;
    set({ title, ...(slugTouched ? {} : { slug: autoSlug(title) }) });
  };

  const handleCoverChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      setCoverUploading(true);
      const res = await adminUploadBlogImage(token, file);
      const url = res.data?.data?.url || '';
      if (url) set({ cover_image: url });
    } catch (err) {
      setError(err?.response?.data?.message || 'Cover upload failed');
    } finally { setCoverUploading(false); }
  };

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (!t || form.tags.includes(t)) { setTagInput(''); return; }
    set({ tags: [...form.tags, t] });
    setTagInput('');
  };

  const toggleCourse = (id) => {
    const next = form.required_courses.includes(id)
      ? form.required_courses.filter(c => c !== id)
      : [...form.required_courses, id];
    set({ required_courses: next });
  };

  const canSave = useMemo(
    () => form.title.trim().length >= 1 && (form.content_html || '').replace(/<[^>]*>/g, '').trim().length >= 1,
    [form.title, form.content_html],
  );

  const handleSave = async (overrideStatus) => {
    setError(''); setSaving(true);
    const payload = { ...form };
    if (overrideStatus) payload.status = overrideStatus;
    if (payload.access_level !== 'premium') payload.required_courses = [];
    try {
      const res = isEdit
        ? await adminUpdateBlog(token, blogId, payload)
        : await adminCreateBlog(token, payload);
      if (res.data?.success) onSaved?.(res.data.data);
      else setError(res.data?.message || 'Save failed');
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="adm-spin-wrap" style={{ minHeight: 320 }}><div className="adm-spinner" /></div>;
  }

  return (
    <div className="be-shell">
      {/* Header */}
      <div className="be-header">
        <div className="be-header-left">
          <button className="adm-icon-btn" title="Back to list" onClick={onClose}><Ic d={ICONS.back} /></button>
          <span className="be-title">{isEdit ? 'Edit Blog' : 'New Blog'}</span>
        </div>
        <div className="be-header-right">
          <button className="adm-btn adm-btn-ghost" disabled={!canSave || saving} onClick={() => handleSave('draft')}>
            <Ic d={ICONS.save} size={14} /> Save Draft
          </button>
          <button className="adm-btn adm-btn-accent" disabled={!canSave || saving} onClick={() => handleSave('published')}>
            <Ic d={ICONS.pub} size={14} /> {form.status === 'published' ? 'Update' : 'Publish'}
          </button>
        </div>
      </div>

      {error && <div className="adm-alert adm-alert-err" style={{ marginBottom: 16 }}>{error}<button onClick={() => setError('')}>✕</button></div>}

      {/* Body grid */}
      <div className="be-body">
        {/* Left — content */}
        <div className="be-main">
          <label className="adm-field adm-field-lg" style={{ marginBottom: 14 }}>
            <span>Title</span>
            <input value={form.title} onChange={onTitleChange} maxLength={220} placeholder="Blog title…" required />
          </label>

          <label className="adm-field adm-field-lg" style={{ marginBottom: 14 }}>
            <span>Slug &nbsp;<em style={{ textTransform: 'none', color: 'var(--text-3)', fontWeight: 400 }}>— URL: /blog/&lt;slug&gt;</em></span>
            <input
              value={form.slug}
              onChange={e => { setSlugTouched(true); set({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }); }}
              placeholder="auto-generated-from-title"
            />
          </label>

          <label className="adm-field adm-field-lg" style={{ marginBottom: 14 }}>
            <span>Excerpt <em style={{ textTransform: 'none', color: 'var(--text-3)', fontWeight: 400 }}>{form.excerpt.length}/320</em></span>
            <textarea
              value={form.excerpt}
              onChange={e => set({ excerpt: e.target.value })}
              maxLength={320}
              rows={2}
              placeholder="Short description shown on listing cards…"
              style={{ resize: 'vertical', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--rs)', color: 'var(--text)', fontFamily: 'var(--fb)', fontSize: 14, padding: '9px 12px', outline: 'none', width: '100%', lineHeight: 1.5 }}
            />
          </label>

          <div style={{ marginBottom: 4 }}>
            <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Content</span>
          </div>
          <div className="be-editor-wrap">
            <RichTextEditor
              initialHtml={form.content_html}
              placeholder="Write something engaging…"
              onChange={({ html, json }) => set({ content_html: html, content_json: json })}
              onImageUpload={async (file) => {
                const res = await adminUploadBlogImage(token, file);
                return { url: res.data?.data?.url };
              }}
            />
          </div>
        </div>

        {/* Right — sidebar panels */}
        <aside className="be-side">

          {/* Cover image */}
          <div className="adm-card" style={{ marginBottom: 14 }}>
            <div className="adm-card-title">Cover Image</div>
            {form.cover_image
              ? <div className="be-cover-wrap">
                  <img src={form.cover_image} alt={form.cover_image_alt || form.title} className="be-cover-img" />
                  <button className="be-cover-del adm-icon-btn" title="Remove cover" onClick={() => set({ cover_image: '' })}>
                    <Ic d={ICONS.del} size={14} />
                  </button>
                </div>
              : <div className="be-cover-placeholder">No cover yet</div>
            }
            <label className="adm-btn adm-btn-ghost" style={{ width: '100%', justifyContent: 'center', cursor: 'pointer', marginTop: 10 }}>
              <Ic d={ICONS.upload} size={14} /> {coverUploading ? 'Uploading…' : 'Upload Cover'}
              <input type="file" hidden accept="image/jpeg,image/png,image/webp,image/gif" disabled={coverUploading} onChange={handleCoverChange} />
            </label>
            <label className="adm-field adm-field-lg" style={{ marginTop: 10 }}>
              <span>Alt text</span>
              <input value={form.cover_image_alt} onChange={e => set({ cover_image_alt: e.target.value })} placeholder="Describe the image…" />
            </label>
          </div>

          {/* Visibility */}
          <div className="adm-card" style={{ marginBottom: 14 }}>
            <div className="adm-card-title">Visibility</div>
            <label className="adm-field adm-field-lg" style={{ marginBottom: 12 }}>
              <span>Status</span>
              <select value={form.status} onChange={e => set({ status: e.target.value })}>
                {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </label>
            <label className="adm-field adm-field-lg">
              <span>Access Level</span>
              <select value={form.access_level} onChange={e => set({ access_level: e.target.value })}>
                {ACCESS_LEVELS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </label>

            {form.access_level === 'premium' && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                  Required courses (any one unlocks)
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {KNOWN_COURSES.map(c => {
                    const sel = form.required_courses.includes(c.id);
                    return (
                      <button key={c.id} onClick={() => toggleCourse(c.id)}
                        className={`adm-tag${sel ? ' adm-tag-accent' : ''}`}
                        style={{ cursor: 'pointer', border: `1px solid ${sel ? 'var(--accent)' : 'var(--border)'}`, background: 'none', fontFamily: 'var(--fb)' }}>
                        {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Taxonomy */}
          <div className="adm-card" style={{ marginBottom: 14 }}>
            <div className="adm-card-title">Taxonomy</div>
            <label className="adm-field adm-field-lg" style={{ marginBottom: 12 }}>
              <span>Category</span>
              <input value={form.category} onChange={e => set({ category: e.target.value })} placeholder="e.g. Trading Tips" />
            </label>
            <label className="adm-field adm-field-lg">
              <span>Tags — press Enter to add</span>
              <input value={tagInput} onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                placeholder="Type a tag…" />
            </label>
            {form.tags.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                {form.tags.map(t => (
                  <span key={t} className="adm-tag" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    {t}
                    <button onClick={() => set({ tags: form.tags.filter(x => x !== t) })}
                      style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', lineHeight: 1, padding: 0, fontSize: 13 }}>✕</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* SEO */}
          <div className="adm-card">
            <div className="adm-card-title">SEO</div>
            <label className="adm-field adm-field-lg" style={{ marginBottom: 12 }}>
              <span>Meta Title</span>
              <input value={form.seo.meta_title} onChange={e => set({ seo: { ...form.seo, meta_title: e.target.value } })} maxLength={80} placeholder="Page title for search engines…" />
            </label>
            <label className="adm-field adm-field-lg">
              <span>Meta Description <em style={{ textTransform: 'none', fontWeight: 400, color: 'var(--text-3)' }}>{form.seo.meta_description.length}/200</em></span>
              <textarea
                value={form.seo.meta_description}
                onChange={e => set({ seo: { ...form.seo, meta_description: e.target.value } })}
                maxLength={200}
                rows={3}
                placeholder="Short description for search results…"
                style={{ resize: 'vertical', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--rs)', color: 'var(--text)', fontFamily: 'var(--fb)', fontSize: 14, padding: '9px 12px', outline: 'none', width: '100%', lineHeight: 1.5 }}
              />
            </label>
          </div>

        </aside>
      </div>
    </div>
  );
};

export default BlogEditor;
